import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { housekeepingApi, usersApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { format } from 'date-fns';
import { 
  Sparkles, BedDouble, ClipboardList, User, Clock, CheckCircle,
  AlertTriangle, Play, Pause, RefreshCw, Plus, Filter, Wand2
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function HousekeepingPage() {
  const { currentProperty } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [users, setUsers] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    task_type: '',
    assigned_to: '',
  });

  const fetchDashboard = useCallback(async () => {
    if (!currentProperty) return;
    try {
      const response = await housekeepingApi.getDashboard(currentProperty.id);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  }, [currentProperty]);

  const fetchTasks = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const params = { scheduled_date: format(new Date(), 'yyyy-MM-dd') };
      if (filters.status) params.status = filters.status;
      if (filters.task_type) params.task_type = filters.task_type;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      
      const response = await housekeepingApi.listTasks(currentProperty.id, params);
      setTasks(response.data || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, filters]);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.list();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchTasks();
    fetchUsers();
  }, [fetchDashboard, fetchTasks]);

  const handleGenerateTasks = async () => {
    try {
      const response = await housekeepingApi.generateTasks(currentProperty.id);
      toast.success(`Generated ${response.data.checkout_cleans + response.data.stayover_cleans} tasks`);
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate tasks');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await housekeepingApi.updateTask(taskId, updates);
      toast.success('Task updated');
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { variant: 'secondary', icon: Clock },
      ASSIGNED: { variant: 'outline', icon: User },
      IN_PROGRESS: { variant: 'default', className: 'bg-blue-500', icon: Play },
      COMPLETED: { variant: 'default', className: 'bg-green-500', icon: CheckCircle },
      VERIFIED: { variant: 'default', className: 'bg-emerald-600', icon: Sparkles },
      CANCELLED: { variant: 'destructive', icon: AlertTriangle },
    };
    const c = config[status] || { variant: 'secondary', icon: Clock };
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className={c.className}>
        <Icon className="h-3 w-3 mr-1" /> {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      LOW: { variant: 'outline' },
      NORMAL: { variant: 'secondary' },
      HIGH: { variant: 'default', className: 'bg-amber-500' },
      URGENT: { variant: 'destructive' },
    };
    const c = config[priority] || { variant: 'secondary' };
    return <Badge variant={c.variant} className={c.className}>{priority}</Badge>;
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      CHECKOUT_CLEAN: 'Checkout Clean',
      STAYOVER_CLEAN: 'Stayover Clean',
      DEEP_CLEAN: 'Deep Clean',
      TURNDOWN: 'Turndown',
      INSPECTION: 'Inspection',
      MINIBAR: 'Minibar',
      GUEST_REQUEST: 'Guest Request',
      MAINTENANCE_REQUEST: 'Maintenance',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Housekeeping</h1>
          <p className="text-muted-foreground">Manage housekeeping tasks for {currentProperty?.name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerateTasks} data-testid="generate-tasks-btn">
            <Wand2 className="h-4 w-4 mr-2" /> Generate Tasks
          </Button>
          <Button onClick={() => setTaskDialogOpen(true)} data-testid="new-task-btn">
            <Plus className="h-4 w-4 mr-2" /> New Task
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BedDouble className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.dirty || 0}</p>
                <p className="text-xs text-muted-foreground">Dirty</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <RefreshCw className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.cleaning || 0}</p>
                <p className="text-xs text-muted-foreground">Cleaning</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.clean || 0}</p>
                <p className="text-xs text-muted-foreground">Clean</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.inspected || 0}</p>
                <p className="text-xs text-muted-foreground">Inspected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ClipboardList className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.tasks?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Tasks Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.tasks?.completed || 0}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            <ClipboardList className="h-4 w-4 mr-2" /> Tasks
          </TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-rooms">
            <BedDouble className="h-4 w-4 mr-2" /> Room Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="ASSIGNED">Assigned</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="VERIFIED">Verified</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.task_type || 'all'} onValueChange={(v) => setFilters({ ...filters, task_type: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Task Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="CHECKOUT_CLEAN">Checkout Clean</SelectItem>
                    <SelectItem value="STAYOVER_CLEAN">Stayover Clean</SelectItem>
                    <SelectItem value="DEEP_CLEAN">Deep Clean</SelectItem>
                    <SelectItem value="TURNDOWN">Turndown</SelectItem>
                    <SelectItem value="INSPECTION">Inspection</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchTasks}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tasks Table */}
          <Card>
            <CardContent className="p-0">
              <Table data-testid="tasks-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Room</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-4" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="no-tasks">
                        <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No tasks for today</p>
                        <p className="text-sm">Click &quot;Generate Tasks&quot; to create based on reservations</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((task) => (
                      <TableRow key={task.id} data-testid={`task-${task.id}`}>
                        <TableCell>
                          <div className="font-medium">#{task.rooms?.room_number || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">{task.rooms?.floor && `Floor ${task.rooms.floor}`}</div>
                        </TableCell>
                        <TableCell>{getTaskTypeLabel(task.task_type)}</TableCell>
                        <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                        <TableCell>
                          {task.users ? (
                            <span>{task.users.first_name} {task.users.last_name}</span>
                          ) : (
                            <Select 
                              value="unassigned" 
                              onValueChange={(v) => v !== 'unassigned' && handleUpdateTask(task.id, { assigned_to: v })}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned" disabled>Unassigned</SelectItem>
                                {users.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(task.status)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{task.notes}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {task.status === 'PENDING' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateTask(task.id, { status: 'IN_PROGRESS' })}>
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            {task.status === 'IN_PROGRESS' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateTask(task.id, { status: 'COMPLETED' })}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {task.status === 'COMPLETED' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateTask(task.id, { status: 'VERIFIED' })}>
                                <Sparkles className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rooms">
          <HousekeepingRooms propertyId={currentProperty?.id} />
        </TabsContent>
      </Tabs>

      {/* New Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <TaskForm 
            propertyId={currentProperty?.id}
            onSuccess={() => { setTaskDialogOpen(false); fetchTasks(); fetchDashboard(); }}
            onCancel={() => setTaskDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HousekeepingRooms({ propertyId }) {
  const [rooms, setRooms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (propertyId) {
      housekeepingApi.getRooms(propertyId).then(res => {
        setRooms(res.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [propertyId]);

  const handleUpdateStatus = async (roomId, status) => {
    try {
      await housekeepingApi.updateRoomStatus(roomId, { housekeeping_status: status });
      toast.success('Room status updated');
      const res = await housekeepingApi.getRooms(propertyId);
      setRooms(res.data);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DIRTY': return 'bg-amber-500 text-white';
      case 'CLEANING': return 'bg-blue-500 text-white';
      case 'CLEAN': return 'bg-green-500 text-white';
      case 'INSPECTED': return 'bg-emerald-600 text-white';
      default: return 'bg-gray-200';
    }
  };

  if (loading) {
    return <Card><CardContent className="py-8"><Skeleton className="h-48" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Room Cleaning Status</CardTitle>
        <CardDescription>Click on a room to update its status</CardDescription>
      </CardHeader>
      <CardContent>
        {rooms?.floors && Object.keys(rooms.floors).length > 0 ? (
          Object.entries(rooms.floors).map(([floor, floorRooms]) => (
            <div key={floor} className="mb-6">
              <h4 className="font-medium mb-3">Floor {floor}</h4>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {floorRooms.map((room) => (
                  <Select
                    key={room.id}
                    value={room.housekeeping_status}
                    onValueChange={(v) => handleUpdateStatus(room.id, v)}
                  >
                    <SelectTrigger className={cn('p-2 h-auto', getStatusColor(room.housekeeping_status))}>
                      <div className="text-center">
                        <div className="font-bold">{room.room_number}</div>
                        <div className="text-xs opacity-80">{room.housekeeping_status}</div>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DIRTY">Dirty</SelectItem>
                      <SelectItem value="CLEANING">Cleaning</SelectItem>
                      <SelectItem value="CLEAN">Clean</SelectItem>
                      <SelectItem value="INSPECTED">Inspected</SelectItem>
                    </SelectContent>
                  </Select>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <BedDouble className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No rooms found</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TaskForm({ propertyId, onSuccess, onCancel }) {
  const [rooms, setRooms] = useState([]);
  const [formData, setFormData] = useState({
    room_id: '',
    task_type: 'OTHER',
    priority: 'NORMAL',
    notes: '',
  });

  useEffect(() => {
    if (propertyId) {
      housekeepingApi.getRooms(propertyId).then(res => {
        setRooms(res.data?.rooms || []);
      });
    }
  }, [propertyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await housekeepingApi.createTask(propertyId, formData);
      toast.success('Task created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="task-form">
      <div className="space-y-2">
        <Label>Room *</Label>
        <Select value={formData.room_id} onValueChange={(v) => setFormData({ ...formData, room_id: v })}>
          <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
          <SelectContent>
            {rooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>#{room.room_number} - {room.room_types?.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Task Type *</Label>
          <Select value={formData.task_type} onValueChange={(v) => setFormData({ ...formData, task_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CHECKOUT_CLEAN">Checkout Clean</SelectItem>
              <SelectItem value="STAYOVER_CLEAN">Stayover Clean</SelectItem>
              <SelectItem value="DEEP_CLEAN">Deep Clean</SelectItem>
              <SelectItem value="TURNDOWN">Turndown</SelectItem>
              <SelectItem value="INSPECTION">Inspection</SelectItem>
              <SelectItem value="GUEST_REQUEST">Guest Request</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="LOW">Low</SelectItem>
              <SelectItem value="NORMAL">Normal</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="URGENT">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
        />
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={!formData.room_id} data-testid="task-submit-btn">Create Task</Button>
      </div>
    </form>
  );
}
